import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { collection, deleteDoc, doc, getDoc, getDocs, query, runTransaction, serverTimestamp, updateDoc, where, writeBatch } from "firebase/firestore";

import { useTheme } from "../../contexts/ThemeContext";
import { useAuth } from "../../contexts/AuthContext";
import { useEmpresa } from "../../contexts/EmpresaContext";
import { nivelUsuario } from "../../utils/permissoes";
import { NIVEIS } from "../../config/niveis";
import LayoutDesktop from "../../layouts/LayoutDesktop";
import FiltroBar from "../../components/FiltroBar/FiltroBar";
import { useSortableTable, SortableHeader } from "../../hooks/useSortableTable";
import { formatData } from "../../utils/dateFormat";
import { db } from "../../config/firebase";

const COLLECTION_PROPOSTAS_INSTALACAO = "financeiro_propostas_instalacoes";
const TIPOS_EQUIPAMENTO = [
  "Pin Load",
  "Plate Load",
  "Banco",
  "Cross",
  "Guiado",
  "Livre",
  "Esteira",
  "Bike",
  "Spinning",
  "Eliptico",
  "Escada",
  "Remo",
];

const STATUS_OPTIONS = [
  { value: "CRIADA", label: "Criada" },
  { value: "ENVIADA", label: "Enviada" },
  { value: "APROVADA", label: "Aprovada" },
  { value: "REPROVADA", label: "Reprovada" },
  { value: "FINALIZADA", label: "Finalizada" },
];

function formatMoney(value) {
  const n = Number(value || 0);
  return n.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function normalizarMapaEquipamentos(input) {
  const base = {};
  TIPOS_EQUIPAMENTO.forEach((tipo) => {
    const qtd = Number(input?.[tipo] || 0);
    base[tipo] = Number.isFinite(qtd) && qtd > 0 ? Math.floor(qtd) : 0;
  });
  return base;
}

async function reservarSequenciaisEquipamentos(grupoId, quantidade) {
  if (!grupoId || quantidade <= 0) return { inicio: 0, fim: 0 };

  const contadorRef = doc(db, "contadores", `equipamentos_${grupoId}`);
  return runTransaction(db, async (tx) => {
    const snap = await tx.get(contadorRef);
    const atual = snap.exists() ? Number(snap.data()?.seq || 0) : 0;
    const inicio = atual + 1;
    const fim = atual + quantidade;

    tx.set(
      contadorRef,
      {
        grupoId,
        seq: fim,
        atualizadoEm: serverTimestamp(),
      },
      { merge: true }
    );

    return { inicio, fim };
  });
}

async function gerarEquipamentosDaPropostaAprovada({ propostaId, propostaData, usuario }) {
  const grupoId = propostaData?.grupoId || usuario?.grupoId || "";
  const clienteId = propostaData?.clienteId || null;
  const clienteNome = propostaData?.clienteNome || "";
  const academiaId = propostaData?.academiaId || null;

  if (!grupoId || !propostaId || !clienteId || !academiaId) {
    return { criados: 0, vinculadosTotal: 0 };
  }

  const mapaObjetivo = normalizarMapaEquipamentos(propostaData?.equipamentosPorTipo || {});
  const objetivoTotal = Object.values(mapaObjetivo).reduce((acc, qtd) => acc + Number(qtd || 0), 0);
  if (objetivoTotal <= 0) {
    return { criados: 0, vinculadosTotal: 0 };
  }

  const snapExistentes = await getDocs(
    query(collection(db, "maquinas"), where("propostaInstalacaoId", "==", propostaId))
  );

  const existentes = snapExistentes.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }));
  const existentesPorTipo = {};
  TIPOS_EQUIPAMENTO.forEach((tipo) => {
    existentesPorTipo[tipo] = 0;
  });

  existentes.forEach((m) => {
    const tipo = String(m?.tipo || "").trim();
    if (!tipo) return;
    if (existentesPorTipo[tipo] == null) existentesPorTipo[tipo] = 0;
    existentesPorTipo[tipo] += 1;
  });

  const deficitsPorTipo = {};
  let totalCriar = 0;
  TIPOS_EQUIPAMENTO.forEach((tipo) => {
    const alvo = Number(mapaObjetivo[tipo] || 0);
    const existente = Number(existentesPorTipo[tipo] || 0);
    const falta = Math.max(0, alvo - existente);
    deficitsPorTipo[tipo] = falta;
    totalCriar += falta;
  });

  const vinculadosTotal = existentes.length + totalCriar;
  if (totalCriar <= 0) {
    return { criados: 0, vinculadosTotal };
  }

  const academiaSnap = await getDoc(doc(db, "setores", academiaId));
  const academiaData = academiaSnap.exists() ? { id: academiaSnap.id, ...academiaSnap.data() } : null;
  const contratoTipoRaw = String(
    academiaData?.tipoContrato || academiaData?.tipo || propostaData?.setorContratoTipo || "Avulso"
  ).trim();
  const setorContratoTipo = contratoTipoRaw || "Avulso";

  const { inicio } = await reservarSequenciaisEquipamentos(grupoId, totalCriar);

  const batch = writeBatch(db);
  let cursor = inicio;

  TIPOS_EQUIPAMENTO.forEach((tipo) => {
    const faltantes = Number(deficitsPorTipo[tipo] || 0);
    if (faltantes <= 0) return;

    const jaExistentesDoTipo = Number(existentesPorTipo[tipo] || 0);
    for (let i = 0; i < faltantes; i += 1) {
      const ordemTipo = jaExistentesDoTipo + i + 1;
      const codigoSequencial = cursor;
      const codigo = `EQ${String(codigoSequencial).padStart(4, "0")}`;
      cursor += 1;

      const maquinaRef = doc(collection(db, "maquinas"));
      batch.set(maquinaRef, {
        codigoSequencial,
        codigo,
        nome: `${tipo} ${String(ordemTipo).padStart(2, "0")}`,
        tipo,
        marca: "",
        modelo: "",
        numeroSerie: "",
        cor: "",
        instaladoEm: "",
        montadoPor: "",
        condicoesIniciais: 3,
        descricao: "",
        fotosUrls: [],
        fotoUrl: "",
        grupoId,
        empresaId: clienteId,
        empresaNome: clienteNome || "",
        setorId: academiaId,
        setorNome: academiaData?.nome || propostaData?.academiaNome || "",
        setorCodigo: academiaData?.codigo || "",
        setorContratoTipo,
        preventivasModeloIds: [],
        checklistInstalacaoAtiva: false,
        checklistInstalacaoId: null,
        checklistInstalacaoNome: "",
        propostaInstalacaoId: propostaId,
        propostaInstalacaoOrigem: true,
        criadoEm: serverTimestamp(),
        criadoPor: usuario?.uid || usuario?.id || "",
        atualizadoEm: serverTimestamp(),
      });
    }
  });

  await batch.commit();
  return { criados: totalCriar, vinculadosTotal };
}

export default function FinanceiroInstalacoesPage() {
  const { tema } = useTheme();
  const { usuario } = useAuth();
  const { empresaAtual, modoGlobal } = useEmpresa();
  const navigate = useNavigate();

  const [registros, setRegistros] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState("");
  const [campoFiltro, setCampoFiltro] = useState("clienteNome");
  const [textoFiltro, setTextoFiltro] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [deletandoId, setDeletandoId] = useState(null);
  const [salvandoStatusId, setSalvandoStatusId] = useState(null);

  useEffect(() => {
    const carregar = async () => {
      if (!usuario?.grupoId) return;
      setCarregando(true);
      setErro("");
      try {
        const nivel = nivelUsuario(usuario);
        const restringirEmpresa = !modoGlobal || nivel < NIVEIS.TECNICO_SENIOR;

        let qBase = query(
          collection(db, COLLECTION_PROPOSTAS_INSTALACAO),
          where("grupoId", "==", usuario.grupoId)
        );

        if (restringirEmpresa) {
          const empresaId = empresaAtual?.id || usuario?.empresaId || null;
          if (empresaId) {
            qBase = query(
              collection(db, COLLECTION_PROPOSTAS_INSTALACAO),
              where("grupoId", "==", usuario.grupoId),
              where("clienteId", "==", empresaId)
            );
          }
        }

        const snap = await getDocs(qBase);
        const lista = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setRegistros(lista);
      } catch (e) {
        console.error("Erro ao carregar propostas de instalação:", e);
        setErro("Erro ao carregar propostas de instalação.");
        setRegistros([]);
      } finally {
        setCarregando(false);
      }
    };

    carregar();
  }, [usuario?.grupoId, usuario?.empresaId, empresaAtual?.id, modoGlobal]);

  const camposFiltro = [
    { value: "clienteNome", label: "Cliente" },
    { value: "academiaNome", label: "Academia" },
    { value: "status", label: "Status" },
    { value: "tipoViagem", label: "Tipo viagem" },
    { value: "previsaoDias", label: "Previsão dias" },
  ];

  const filtrados = useMemo(() => {
    const texto = textoFiltro.trim().toLowerCase();
    if (!texto) return registros;

    return registros.filter((r) => {
      const base = String(r?.[campoFiltro] ?? "").toLowerCase();
      return base.includes(texto);
    });
  }, [registros, campoFiltro, textoFiltro]);

  const { sortedData, requestSort, getSortDirection } = useSortableTable(
    filtrados,
    "clienteNome"
  );

  const handleExcluir = async (registro) => {
    if (!registro?.id) return;
    const confirmar = window.confirm("Deseja excluir esta proposta de instalação?");
    if (!confirmar) return;

    setDeletandoId(registro.id);
    setErro("");
    try {
      await deleteDoc(doc(db, COLLECTION_PROPOSTAS_INSTALACAO, registro.id));
      setRegistros((prev) => prev.filter((item) => item.id !== registro.id));
    } catch (e) {
      console.error("Erro ao excluir proposta:", e);
      setErro("Erro ao excluir proposta de instalação.");
    } finally {
      setDeletandoId(null);
    }
  };

  const handleAlterarStatus = async (registro, novoStatus) => {
    if (!registro?.id || !novoStatus) return;
    if (String(novoStatus) === String(registro.status || "")) return;

    setSalvandoStatusId(registro.id);
    setErro("");
    try {
      let resumoGeracao = { criados: 0, vinculadosTotal: 0 };

      if (String(novoStatus || "").trim().toUpperCase() === "APROVADA") {
        resumoGeracao = await gerarEquipamentosDaPropostaAprovada({
          propostaId: registro.id,
          propostaData: { ...registro, status: novoStatus },
          usuario,
        });
      }

      await updateDoc(doc(db, COLLECTION_PROPOSTAS_INSTALACAO, registro.id), {
        status: novoStatus,
        ...(String(novoStatus || "").trim().toUpperCase() === "APROVADA"
          ? {
              equipamentosGeradosAutomaticamente: true,
              equipamentosGeradosEm: serverTimestamp(),
              equipamentosGeradosUltimoLote: Number(resumoGeracao?.criados || 0),
              equipamentosGeradosTotalVinculados: Number(resumoGeracao?.vinculadosTotal || 0),
            }
          : {}),
        updatedAt: serverTimestamp(),
      });

      setRegistros((prev) => prev.map((item) => (
        item.id === registro.id
          ? {
              ...item,
              status: novoStatus,
              ...(String(novoStatus || "").trim().toUpperCase() === "APROVADA"
                ? {
                    equipamentosGeradosAutomaticamente: true,
                    equipamentosGeradosUltimoLote: Number(resumoGeracao?.criados || 0),
                    equipamentosGeradosTotalVinculados: Number(resumoGeracao?.vinculadosTotal || 0),
                  }
                : {}),
            }
          : item
      )));

      if (String(novoStatus || "").trim().toUpperCase() === "APROVADA") {
        setErro("");
      }
    } catch (e) {
      console.error("Erro ao atualizar status da proposta de instalação:", e);
      setErro("Erro ao atualizar status da proposta de instalação.");
    } finally {
      setSalvandoStatusId(null);
    }
  };

  return (
    <LayoutDesktop tema={tema} titulo="Propostas" subtitulo="Financeiro / Propostas / Instalações">
      <section className="pc-card pc-table-card">
        <header className="pc-table-header">
          <div className="pc-table-header-text">
            <h3>Instalações</h3>
            <p>Propostas de instalação com escopo de equipamentos e orçamento.</p>
          </div>
          <div className="pc-table-header-actions">
            <button
              type="button"
              className="pc-btn-primario"
              onClick={() => navigate("/financeiro/instalacoes/nova")}
            >
              + Nova instalação
            </button>
          </div>
        </header>

        <FiltroBar
          campos={camposFiltro}
          campoSelecionado={campoFiltro}
          onCampoChange={setCampoFiltro}
          textoFiltro={textoFiltro}
          onTextoChange={setTextoFiltro}
          placeholder="Filtrar instalações..."
          mostrarAdicionar={false}
        />

        <div className="pc-table-wrapper">
          {erro && (
            <div style={{ background: "#7f1d1d", color: "#fecaca", padding: 8, borderRadius: 8, marginBottom: 12, fontSize: 14 }}>
              {erro}
            </div>
          )}

          {carregando ? (
            <p style={{ padding: 10 }}>Carregando propostas...</p>
          ) : sortedData.length === 0 ? (
            <p style={{ padding: 10 }}>Nenhuma proposta de instalação encontrada.</p>
          ) : (
            <table className="pc-table pc-table-expandable">
              <thead>
                <tr>
                  <th style={{ width: 44 }} />
                  <SortableHeader
                    label="Cliente"
                    sortKey="clienteNome"
                    onSort={requestSort}
                    sortDirection={getSortDirection("clienteNome")}
                  />
                  <SortableHeader
                    label="Academia"
                    sortKey="academiaNome"
                    onSort={requestSort}
                    sortDirection={getSortDirection("academiaNome")}
                  />
                  <SortableHeader
                    label="Tipo viagem"
                    sortKey="tipoViagem"
                    onSort={requestSort}
                    sortDirection={getSortDirection("tipoViagem")}
                  />
                  <SortableHeader
                    label="Status"
                    sortKey="status"
                    onSort={requestSort}
                    sortDirection={getSortDirection("status")}
                  />
                  <th>Equipamentos</th>
                  <th>Previsão (dias)</th>
                  <th>Valor total (R$)</th>
                  <th style={{ width: 300 }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {sortedData.map((r) => {
                  const aberto = expandedId === r.id;
                  return (
                    <React.Fragment key={r.id}>
                      <tr className={aberto ? "pc-row-open" : undefined}>
                        <td>
                          <button
                            type="button"
                            className="pc-expand-btn"
                            aria-label={aberto ? "Recolher" : "Expandir"}
                            onClick={() => setExpandedId((prev) => (prev === r.id ? null : r.id))}
                          >
                            <span className={aberto ? "pc-expand-chevron open" : "pc-expand-chevron"}>▸</span>
                          </button>
                        </td>
                        <td>{r.clienteNome || "-"}</td>
                        <td>{r.academiaNome || "-"}</td>
                        <td>{r.tipoViagem || "-"}</td>
                        <td>
                          {STATUS_OPTIONS.find((s) => s.value === r.status)?.label || r.status || "-"}
                        </td>
                        <td>{r.equipamentosTotal ?? 0}</td>
                        <td>{r.previsaoDias ?? "-"}</td>
                        <td>{formatMoney(r.valorTotal || 0)}</td>
                        <td>
                          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                            <select
                              value={r.status || "CRIADA"}
                              className="pc-input"
                              title="Alterar status"
                              disabled={deletandoId === r.id || salvandoStatusId === r.id}
                              onChange={(e) => handleAlterarStatus(r, e.target.value)}
                              style={{ minWidth: 120, height: 32, padding: "4px 8px" }}
                            >
                              {STATUS_OPTIONS.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                  {opt.label}
                                </option>
                              ))}
                            </select>
                            <button
                              type="button"
                              className="pc-btn-secundario"
                              onClick={() => navigate(`/financeiro/instalacoes/editar/${r.id}`)}
                              disabled={deletandoId === r.id || salvandoStatusId === r.id}
                            >
                              Editar
                            </button>
                            <button
                              type="button"
                              className="pc-btn-secundario"
                              onClick={() => handleExcluir(r)}
                              disabled={deletandoId === r.id || salvandoStatusId === r.id}
                            >
                              {deletandoId === r.id ? "Excluindo..." : "Excluir"}
                            </button>
                          </div>
                        </td>
                      </tr>

                      {aberto && (
                        <tr className="pc-expanded-row">
                          <td colSpan={9}>
                            <div className="pc-expand-panel">
                              <div className="pc-expand-grid">
                                <div className="pc-expand-item">
                                  <div className="pc-expand-label">Cliente</div>
                                  <div className="pc-expand-value">{r.clienteNome || "-"}</div>
                                </div>
                                <div className="pc-expand-item">
                                  <div className="pc-expand-label">Academia</div>
                                  <div className="pc-expand-value">{r.academiaNome || "-"}</div>
                                </div>
                                <div className="pc-expand-item">
                                  <div className="pc-expand-label">Status</div>
                                  <div className="pc-expand-value">
                                    <select
                                      value={r.status || "CRIADA"}
                                      className="pc-input"
                                      title="Alterar status"
                                      disabled={deletandoId === r.id || salvandoStatusId === r.id}
                                      onChange={(e) => handleAlterarStatus(r, e.target.value)}
                                      style={{ minWidth: 140, height: 32, padding: "4px 8px" }}
                                    >
                                      {STATUS_OPTIONS.map((opt) => (
                                        <option key={opt.value} value={opt.value}>
                                          {opt.label}
                                        </option>
                                      ))}
                                    </select>
                                  </div>
                                </div>
                                <div className="pc-expand-item">
                                  <div className="pc-expand-label">Endereço destino</div>
                                  <div className="pc-expand-value">{r.enderecoDestino || "-"}</div>
                                </div>
                                <div className="pc-expand-item">
                                  <div className="pc-expand-label">Data criação</div>
                                  <div className="pc-expand-value">{formatData(r.createdAt) || "-"}</div>
                                </div>
                                <div className="pc-expand-item">
                                  <div className="pc-expand-label">Tipo de viagem</div>
                                  <div className="pc-expand-value">{r.tipoViagem || "-"}</div>
                                </div>
                                <div className="pc-expand-item">
                                  <div className="pc-expand-label">Previsão de dias</div>
                                  <div className="pc-expand-value">{r.previsaoDias ?? "-"}</div>
                                </div>
                                <div className="pc-expand-item">
                                  <div className="pc-expand-label">Ajudantes</div>
                                  <div className="pc-expand-value">{r.quantidadeAjudantes ?? 0}</div>
                                </div>
                                <div className="pc-expand-item">
                                  <div className="pc-expand-label">Gastos almoço (R$)</div>
                                  <div className="pc-expand-value">{formatMoney(r.gastosAlmoco || 0)}</div>
                                </div>
                                <div className="pc-expand-item">
                                  <div className="pc-expand-label">Km rodados</div>
                                  <div className="pc-expand-value">{r.kmRodados ?? 0}</div>
                                </div>
                                <div className="pc-expand-item">
                                  <div className="pc-expand-label">Pedágio (R$)</div>
                                  <div className="pc-expand-value">{formatMoney(r.valorPedagio || 0)}</div>
                                </div>
                                <div className="pc-expand-item">
                                  <div className="pc-expand-label">Deslocamento (R$)</div>
                                  <div className="pc-expand-value">{formatMoney(r.valorDeslocamento || 0)}</div>
                                </div>
                                <div className="pc-expand-item">
                                  <div className="pc-expand-label">Mão de obra (R$)</div>
                                  <div className="pc-expand-value">{formatMoney(r.valorMaoObra || 0)}</div>
                                </div>
                                <div className="pc-expand-item">
                                  <div className="pc-expand-label">Peças (R$)</div>
                                  <div className="pc-expand-value">{formatMoney(r.valorPecas || 0)}</div>
                                </div>
                                <div className="pc-expand-item">
                                  <div className="pc-expand-label">Desconto (R$)</div>
                                  <div className="pc-expand-value">{formatMoney(r.valorDesconto || 0)}</div>
                                </div>
                                <div className="pc-expand-item">
                                  <div className="pc-expand-label">Valor total (R$)</div>
                                  <div className="pc-expand-value">{formatMoney(r.valorTotal || 0)}</div>
                                </div>
                                <div className="pc-expand-item" style={{ gridColumn: "1 / -1" }}>
                                  <div className="pc-expand-label">Equipamentos por tipo</div>
                                  <div className="pc-expand-value">
                                    {Object.entries(r.equipamentosPorTipo || {})
                                      .filter(([, qtd]) => Number(qtd || 0) > 0)
                                      .map(([tipo, qtd]) => `${tipo}: ${qtd}`)
                                      .join(" • ") || "-"}
                                  </div>
                                </div>
                                <div className="pc-expand-item" style={{ gridColumn: "1 / -1" }}>
                                  <div className="pc-expand-label">Observações</div>
                                  <div className="pc-expand-value">{r.observacoes || "-"}</div>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </section>
    </LayoutDesktop>
  );
}
